"use client";

import { Fragment, useCallback, useEffect, useRef, useState } from "react";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import SplitText from "gsap/SplitText";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { stegaClean } from "@sanity/client/stega";
import { ImageElement } from "../Image";
import { HeroPeopleProps } from "./heroPeople";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(useGSAP);

export const HeroPeopleMobile = ({ title, people }: HeroPeopleProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const imageWrapperRef = useRef<HTMLDivElement>(null);

    const [activePersonIndex, setActivePersonIndex] = useState<number | null>(0);

    const cleanTitle = stegaClean(title);

    const personWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleOutSideClick = (event: any) => {
            if (!personWrapperRef.current?.contains(event.target)) {
                setActivePersonIndex(null);
            }
        };

        window.addEventListener("click", handleOutSideClick);

        return () => {
            window.removeEventListener("click", handleOutSideClick);
        };
    }, [personWrapperRef]);

    useGSAP(
        () => {
            const heroText = SplitText.create(".and", {
                type: "lines, words",
                mask: "lines",
            });

            gsap.set(heroText.words, { y: "110%" });

            gsap.to(heroText.words, {
                duration: 1,
                y: 0,
                stagger: 0.075,
                ease: "power4.out",
                delay: 1.5,
                mask: "lines",
            });
        },
        { scope: containerRef },
    );

    const [position, setPosition] = useState(0);

    const setPerson = useCallback((index: number) => {
        setActivePersonIndex(index);

        const el = document.querySelector(`.person-${index}`) as HTMLElement;

        const factor = document.querySelector(`.fact-${index}`) as HTMLElement;
        factor.classList.add("active");

        const position = el.offsetTop + el.clientHeight * 2;

        if (el.getBoundingClientRect().top + 260 + el.clientHeight * 2 > window.innerHeight) {
            setPosition(el.offsetTop - 260);
        } else {
            const height = imageWrapperRef.current?.clientHeight || 0;

            if (index > people.length - 8) {
                setPosition(el.offsetTop - height);
            } else {
                setPosition(position);
            }
        }
    }, []);

    useEffect(() => {
        setPerson(0);
    }, []);

    return (
        <div className="container hero-people intersection-hero" ref={containerRef}>
            <div className="pb-20 mx-2.5 md:mx-5 container-inner">
                <div className="relative grid grid-cols-12 gap-5 mt-28">
                    <div
                        style={{ "--image-top": position + "px" } as React.CSSProperties}
                        className={`absolute right-0 top-[var(--image-top)] flex  w-[185.876px] left-column`}
                    >
                        <div className="relative flex justify-center w-full wrapper max-h-[calc(100vh-150px)]">
                            <div className="relative w-full">
                                {people?.map((person, index) => (
                                    <div
                                        className={`absolute top-0 left-1/2 -translate-x-1/2 w-full ${activePersonIndex === index ? "opacity-100" : "opacity-0"} overflow-hidden max-w-[185px] `}
                                        key={index}
                                        ref={imageWrapperRef}
                                    >
                                        {person.image && person.image.image && (
                                            <ImageElement
                                                src={person.image?.image}
                                                width={800}
                                                alt={person.image.alt || ""}
                                                className={`object-cover max-h-[calc(90vh-150px)] aspect-portrait`}
                                            />
                                        )}
                                        <div className={`fact bg-[#F351FF] flex`}>
                                            <div
                                                className={`fact-${index} w-full m-body-m  flex whitespace-nowrap py-1`}
                                            >
                                                <span className="px-1">{person.facts}</span>
                                                <span className="">{person.facts}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="col-span-12 right-column">
                        <p className="mb-6 m-display-2 and">{cleanTitle}</p>

                        <div ref={personWrapperRef}>
                            {people?.map((person, index) => (
                                <Fragment key={index}>
                                    <div key={index} className="flex items-center gap-2">
                                        <p
                                            className={`m-headline-3 person-${index}`}
                                            onClick={() => setPerson(index)}
                                        >
                                            {person.name}
                                        </p>
                                        {activePersonIndex === index && <img src="/icon_dot.svg" />}
                                    </div>
                                </Fragment>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
